const TITLE: Record<"polygon" | "rectangle" | "circle" | "line" | "image", string> = {
  rectangle: "Rectangle",
  polygon: "Polygone",
  circle: "Cercle",
  line: "Ligne",
  image: "Image",
};

const STEP_TEXT: Record<"polygon" | "rectangle" | "circle" | "line" | "image", Record<1 | 2, string>> = {
  rectangle: {
    1: "Cliquez sur la carte pour placer le premier coin du rectangle.",
    2: "Déplacez la souris puis cliquez pour valider le coin opposé.",
  },
  polygon: {
    1: "Cliquez sur la carte pour placer le premier sommet du polygone.",
    2: "Cliquez pour ajouter un sommet, ou cliquez sur le premier sommet pour fermer le polygone.",
  },
  circle: {
    1: "Cliquez sur la carte pour placer le centre du cercle.",
    2: "Déplacez la souris puis cliquez pour valider le rayon.",
  },
  line: {
    1: "Cliquez sur la carte pour placer le premier point de la ligne.",
    2: "Cliquez pour ajouter un point, ou cliquez sur le dernier point posé pour terminer la ligne.",
  },
  image: {
    1: "Cliquez sur la carte pour placer le premier coin de l'image.",
    2: "Déplacez la souris puis cliquez pour valider le coin opposé (le ratio de l'image est conservé).",
  },
};

/**
 * Tutoriel générique affiché pendant un mode interactif de `@tokamap/core` (dessin d'une forme,
 * édition de la vue par défaut de la carte...) — un seul widget partagé par tous ces modes :
 * `showMessage` affiche un badge (mis en avant visuellement), un titre et un texte quelconques ;
 * `showStep` est une spécialisation pour le dessin (rectangle/image : 2 clics, premier coin puis
 * coin opposé ; cercle : 2 clics, centre puis point du bord — texte distinct pour chacun malgré
 * le geste identique ; polygone : un clic par sommet, puis un clic sur le premier sommet pour
 * fermer ; ligne : un clic par sommet comme le polygone, mais un clic sur le DERNIER sommet posé
 * pour terminer, puisqu'une ligne ne se referme pas), qui
 * délègue à `showMessage` avec le badge `N/2` et le texte adaptés à `shapeKind`/`step`. Piloté par
 * `createTokaMap`, qui choisit quand l'appeler/le masquer selon le mode et ses propres options
 * (ex. `StartDrawingOptions.skipTutorial` pour le dessin).
 */
export class DrawTutorial {
  private element: HTMLDivElement | null = null;
  private badgeElement: HTMLDivElement | null = null;
  private titleElement: HTMLDivElement | null = null;
  private textElement: HTMLDivElement | null = null;

  constructor(private container: HTMLElement) {}

  showMessage(badge: string, title: string, text: string): void {
    if (!this.element) {
      this.element = document.createElement("div");
      this.element.className = "toka-draw-tutorial";

      this.badgeElement = document.createElement("div");
      this.badgeElement.className = "toka-draw-tutorial__step";
      this.element.appendChild(this.badgeElement);

      const body = document.createElement("div");
      body.className = "toka-draw-tutorial__body";
      this.titleElement = document.createElement("div");
      this.titleElement.className = "toka-draw-tutorial__title";
      body.appendChild(this.titleElement);
      this.textElement = document.createElement("div");
      this.textElement.className = "toka-draw-tutorial__text";
      body.appendChild(this.textElement);
      this.element.appendChild(body);

      this.container.appendChild(this.element);
    }
    this.badgeElement!.textContent = badge;
    this.titleElement!.textContent = title;
    this.textElement!.textContent = text;
  }

  showStep(shapeKind: "polygon" | "rectangle" | "circle" | "line" | "image", step: 1 | 2): void {
    this.showMessage(`${step}/2`, TITLE[shapeKind], STEP_TEXT[shapeKind][step]);
  }

  hide(): void {
    this.element?.remove();
    this.element = null;
    this.badgeElement = null;
    this.titleElement = null;
    this.textElement = null;
  }
}
